import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  GENERATION_ATTACHMENT_ROLES,
  GENERATION_FOLLOW_UPS,
  type GenerationAttachmentRole,
  type GenerationFollowUp,
  type GenerationGoal,
  type GenerationOptions,
  type PreviewRouteInput,
} from '@mpr/shared';

import { api, ApiError } from '../lib/api.js';
import {
  buildCreateBody,
  buildPreviewBody,
  carryOptions,
  defaultRoleFor,
  defaultsFor,
  GOAL_ORDER,
  isHttpsUrl,
  mergeOptions,
  normalizeFollowUp,
  pruneOptions,
  requestProblems,
  stepLabelKey,
  type AttachmentDraft,
} from '../lib/studioGoal.js';
import type { StudioHandoff } from '../lib/studioProject.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';
import { OptionsPanel } from './StudioOptions.js';
import { ProjectDetail } from './StudioProjects.js';

/** A value that follows another one after it has stopped changing for a moment. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

/**
 * The studio's default way in: pick the end product, say what is wanted,
 * attach references, tune the choices that matter, and start. The server
 * writes the first text step(s); the project page then follows them and offers
 * the next step (making the picture, voice or video) once the prompt is good.
 *
 * Nothing here decides which connector runs — the platform does, and the line
 * under the choices says which one it will be (or that none is set up).
 */
export function GoalCompose({
  onSendToPublish,
  onOpenScheduled,
  onOpenConnectors,
}: {
  onSendToPublish: (handoff: StudioHandoff) => void;
  onOpenScheduled?: () => void;
  onOpenConnectors?: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [goal, setGoal] = useState<GenerationGoal | null>(null);
  const [followUp, setFollowUpState] = useState<GenerationFollowUp>('NONE');
  const [requestText, setRequestText] = useState('');
  const [options, setOptions] = useState<GenerationOptions>({});
  const [attachments, setAttachments] = useState<AttachmentDraft[]>([]);
  const [urlText, setUrlText] = useState('');
  const [urlError, setUrlError] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [presetId, setPresetId] = useState('');
  const [projectId, setProjectId] = useState<string | null>(null);

  const reset = () => {
    setProjectId(null);
    setGoal(null);
    setFollowUpState('NONE');
    setRequestText('');
    setOptions({});
    setAttachments([]);
    setUrlText('');
    setUrlError(false);
    setUploadError(null);
    setPresetId('');
  };

  // The same per-plan cap the free mode applies to its reference files.
  const account = useQuery({ queryKey: ['account'], queryFn: api.account });
  const attachmentLimit = account.data?.usage['GENERATION_ATTACHMENTS_MAX']?.limit ?? null;
  const atCap = attachmentLimit !== null && attachments.length >= attachmentLimit;

  const chooseGoal = (next: GenerationGoal) => {
    if (next === goal) return;
    const nextFollowUp = normalizeFollowUp(next, followUp);
    setGoal(next);
    setFollowUpState(nextFollowUp);
    // A different goal starts from its own defaults; only who it is for carries over.
    setOptions(mergeOptions(defaultsFor(next, nextFollowUp), carryOptions(options)));
    setPresetId('');
  };

  const setFollowUp = (next: GenerationFollowUp) => {
    if (!goal) return;
    setFollowUpState(next);
    setOptions(mergeOptions(defaultsFor(goal, next), options));
    setPresetId('');
  };

  /* ---- Attachments ---- */

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    event.target.value = '';
    if (files.length === 0 || !goal) return;
    setUploadError(null);

    let count = attachments.length;
    for (const file of files) {
      if (attachmentLimit !== null && count >= attachmentLimit) break;
      const placeholder: AttachmentDraft = {
        key: `pending-${crypto.randomUUID()}`,
        source: 'file',
        label: file.name,
        mimeType: file.type,
        uploading: true,
        role: defaultRoleFor(goal, followUp, count),
      };
      count += 1;
      setAttachments((current) => [...current, placeholder]);
      try {
        const { asset } = await api.uploadGenerationMedia(file);
        setAttachments((current) =>
          current.map((entry) =>
            entry.key === placeholder.key
              ? { ...entry, mediaId: asset.id, label: asset.filename, mimeType: asset.mimeType, uploading: false }
              : entry,
          ),
        );
      } catch (cause) {
        setAttachments((current) => current.filter((entry) => entry.key !== placeholder.key));
        setUploadError(cause instanceof ApiError ? cause.message : t('studio.uploadFailed'));
      }
    }
  };

  const addUrl = () => {
    if (!goal) return;
    if (!isHttpsUrl(urlText)) {
      setUrlError(true);
      return;
    }
    setUrlError(false);
    setAttachments((current) => [
      ...current,
      {
        key: `url-${crypto.randomUUID()}`,
        source: 'url',
        url: urlText.trim(),
        label: urlText.trim(),
        role: defaultRoleFor(goal, followUp, current.length),
      },
    ]);
    setUrlText('');
  };

  const setRole = (key: string, role: GenerationAttachmentRole) =>
    setAttachments((current) => current.map((entry) => (entry.key === key ? { ...entry, role } : entry)));

  /* ---- Which connectors would run ---- */

  const previewBody = useMemo(
    () => (goal ? buildPreviewBody({ goal, followUp, options, attachments }) : null),
    [goal, followUp, options, attachments],
  );
  const previewKey = useDebounced(JSON.stringify(previewBody), 400);
  const preview = useQuery({
    queryKey: ['generation-route-preview', previewKey],
    queryFn: () => api.previewGenerationRoute(JSON.parse(previewKey) as PreviewRouteInput),
    enabled: previewKey !== 'null',
    staleTime: 30_000,
    retry: false,
  });

  /* ---- Saved sets of choices ---- */

  const presets = useQuery({
    queryKey: ['generation-option-presets'],
    queryFn: api.listGenerationOptionPresets,
  });
  const savedPresets = presets.data ?? [];

  const loadPreset = (id: string) => {
    setPresetId(id);
    const preset = savedPresets.find((p) => p.id === id);
    if (!preset) return;
    const { attachments: _ignored, ...saved } = preset.options;
    const nextFollowUp = normalizeFollowUp(preset.goal, preset.followUp);
    setGoal(preset.goal);
    setFollowUpState(nextFollowUp);
    setOptions(mergeOptions(defaultsFor(preset.goal, nextFollowUp), saved));
  };

  const savePreset = useMutation({
    mutationFn: async () => {
      if (!goal) return null;
      const name = window.prompt(t('studio.preset.namePrompt'))?.trim();
      if (!name) return null;
      // The references belong to this request, not to the way of working.
      return api.saveGenerationOptionPreset({
        name,
        goal,
        followUp,
        options: pruneOptions(options, goal, followUp, []),
      });
    },
    onSuccess: (saved) => {
      if (!saved) return;
      setPresetId(saved.id);
      void queryClient.invalidateQueries({ queryKey: ['generation-option-presets'] });
    },
  });

  const deletePreset = useMutation({
    mutationFn: (id: string) => api.deleteGenerationOptionPreset(id),
    onSuccess: () => {
      setPresetId('');
      void queryClient.invalidateQueries({ queryKey: ['generation-option-presets'] });
    },
  });

  /* ---- Start ---- */

  const problems = goal ? requestProblems({ goal, followUp, requestText, options, attachments }) : [];

  const create = useMutation({
    mutationFn: async () => {
      if (!goal) throw new Error('no goal');
      return api.createGenerationProject(buildCreateBody({ goal, followUp, requestText, options, attachments }));
    },
    onSuccess: (project) => {
      // The page it opens on already has the first steps; polling takes over from here.
      queryClient.setQueryData(['generation-project', project.id], project);
      void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
      setProjectId(project.id);
    },
  });

  if (projectId) {
    return (
      <ProjectDetail
        projectId={projectId}
        backLabel="studio.project.newRequest"
        onBack={reset}
        onSendToPublish={onSendToPublish}
        onOpenScheduled={onOpenScheduled}
      />
    );
  }

  const steps = preview.data ?? [];

  return (
    <div className="goal-compose">
      <h3>{t('studio.goal.title')}</h3>
      <div className="goal-cards" role="group" aria-label={t('studio.goal.title')}>
        {GOAL_ORDER.map((candidate) => (
          <button
            key={candidate}
            type="button"
            className={`goal-card${goal === candidate ? ' selected' : ''}`}
            aria-pressed={goal === candidate}
            onClick={() => chooseGoal(candidate)}
          >
            <strong>{t(`studio.goal.${candidate}` as TranslationKey)}</strong>
            <span className="muted small">{t(`studio.goal.${candidate}.desc` as TranslationKey)}</span>
          </button>
        ))}
      </div>

      {goal && (
        <>
          {goal === 'POST' && (
            <div className="goal-field">
              <label htmlFor="goal-followup">{t('studio.followUp.label')}</label>
              <select
                id="goal-followup"
                value={followUp}
                onChange={(event) => setFollowUp(event.target.value as GenerationFollowUp)}
              >
                {GENERATION_FOLLOW_UPS.map((choice) => (
                  <option key={choice} value={choice}>
                    {t(`studio.followUp.${choice}` as TranslationKey)}
                  </option>
                ))}
              </select>
              <p className="muted tiny goal-hint">{t('studio.followUp.hint')}</p>
            </div>
          )}

          <label htmlFor="goal-request">{t('studio.goal.requestLabel')}</label>
          <textarea
            id="goal-request"
            dir="auto"
            rows={5}
            value={requestText}
            onChange={(event) => setRequestText(event.target.value)}
            placeholder={t('studio.goal.requestPlaceholder')}
          />

          <div className="studio-attachments">
            <div className="usage-head">
              <span>{t('studio.att.title')}</span>
              <span className="mono small">
                {attachmentLimit === null
                  ? `${attachments.length} / ∞`
                  : `${attachments.length} / ${attachmentLimit}`}
              </span>
            </div>
            <p className="muted small">{t('studio.att.hint')}</p>

            {attachments.length > 0 && (
              <ul className="asset-list goal-attachments">
                {attachments.map((attachment) => (
                  <li key={attachment.key}>
                    <span className="goal-attachment-name small" dir="auto">
                      {attachment.source === 'url' ? <Icon.link /> : <Icon.image />}
                      <span>{attachment.label}</span>
                    </span>
                    {attachment.uploading ? (
                      <span className="tiny muted">{t('studio.uploading')}</span>
                    ) : (
                      <>
                        <select
                          aria-label={`${t('studio.att.role')} — ${attachment.label}`}
                          value={attachment.role}
                          onChange={(event) =>
                            setRole(attachment.key, event.target.value as GenerationAttachmentRole)
                          }
                        >
                          {GENERATION_ATTACHMENT_ROLES.map((role) => (
                            <option key={role} value={role}>
                              {t(`studio.role.${role}` as TranslationKey)}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="ghost small"
                          onClick={() => setAttachments((current) => current.filter((a) => a.key !== attachment.key))}
                        >
                          {t('common.remove')}
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {uploadError && <p className="error small">{uploadError}</p>}

            <label htmlFor="goal-upload">{t('studio.att.upload')}</label>
            <input
              id="goal-upload"
              type="file"
              accept="image/*,video/*,audio/*"
              multiple
              disabled={atCap}
              onChange={upload}
            />
            <div className="goal-url-row">
              <input
                type="url"
                className="ltr"
                dir="ltr"
                aria-label={t('studio.att.link')}
                placeholder={t('studio.att.urlPlaceholder')}
                value={urlText}
                disabled={atCap}
                onChange={(event) => {
                  setUrlText(event.target.value);
                  setUrlError(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addUrl();
                  }
                }}
              />
              <button type="button" className="ghost" onClick={addUrl} disabled={atCap || !urlText.trim()}>
                {t('studio.att.addUrl')}
              </button>
            </div>
            {urlError && <p className="error small">{t('studio.att.urlInvalid')}</p>}
            {atCap && <p className="muted small">{t('studio.attachmentsCapped')}</p>}
          </div>

          <div className="goal-presets">
            <label htmlFor="goal-preset">{t('studio.preset.label')}</label>
            <div className="goal-preset-row">
              <select id="goal-preset" value={presetId} onChange={(event) => loadPreset(event.target.value)}>
                <option value="">{t('studio.preset.none')}</option>
                {savedPresets.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="ghost small"
                onClick={() => savePreset.mutate()}
                disabled={savePreset.isPending}
              >
                {t('studio.preset.save')}
              </button>
              {presetId && (
                <button
                  type="button"
                  className="danger small"
                  disabled={deletePreset.isPending}
                  onClick={() => {
                    if (window.confirm(t('studio.preset.deleteConfirm'))) deletePreset.mutate(presetId);
                  }}
                >
                  <Icon.trash />
                  {t('studio.preset.delete')}
                </button>
              )}
            </div>
            {savePreset.isError && <ErrorNotice error={savePreset.error} />}
            {deletePreset.isError && <ErrorNotice error={deletePreset.error} />}
          </div>

          <OptionsPanel goal={goal} followUp={followUp} value={options} onChange={setOptions} />

          <section className="goal-route" aria-live="polite">
            <h3>{t('studio.route.title')}</h3>
            {preview.isFetching && steps.length === 0 && (
              <p className="muted small">{t('studio.route.checking')}</p>
            )}
            {preview.isError && <p className="muted small">{t('studio.route.failed')}</p>}
            <ul className="goal-route-steps">
              {steps.map((step, index) => (
                <li key={`${step.stage}-${index}`}>
                  <strong className="small">{t(stepLabelKey(step))}</strong>
                  {step.connector ? (
                    <span className="badge ok">{t('studio.route.uses', { name: step.connector.name })}</span>
                  ) : (
                    <div className="notice warn goal-route-warning" role="alert">
                      <span>
                        {step.reason
                          ? t('studio.route.missing', { reason: step.reason })
                          : t('studio.route.missingNoReason')}
                      </span>
                      {onOpenConnectors && (
                        <button type="button" className="link tiny" onClick={onOpenConnectors}>
                          {t('studio.route.fix')}
                        </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {problems
            .filter((problem) => problem !== 'REQUEST_EMPTY')
            .map((problem) => (
              <p key={problem} className="error small">
                {t(`studio.problem.${problem}` as TranslationKey)}
              </p>
            ))}
          {create.isError && <ErrorNotice error={create.error} />}

          <div className="actions">
            <button
              type="button"
              className="primary"
              disabled={problems.length > 0 || create.isPending}
              onClick={() => {
                if (problems.length === 0) create.mutate();
              }}
            >
              <Icon.sparkle />
              {create.isPending ? t('studio.create.starting') : t('studio.create.start')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
